import React, { useState } from 'react';
import { sessionsAPI } from '../../services/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { TableRow, TableCell } from '../ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Edit2 } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

const SESSION_STATUS_COLORS = {
  scheduled: 'bg-blue-100 text-blue-800',
  in_progress: 'bg-violet-100 text-violet-800',
  completed: 'bg-green-100 text-green-800',
  cancelled: 'bg-red-100 text-red-800',
};

export const AttendanceRow = ({ session, onSave }) => {
  const [editing, setEditing] = useState(false);
  const [expected, setExpected] = useState(String(session.expected_count ?? ''));
  const [registered, setRegistered] = useState(String(session.registered_count ?? ''));
  const [actual, setActual] = useState(String(session.attended_count ?? ''));
  const [saving, setSaving] = useState(false);
  const [statusSaving, setStatusSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await sessionsAPI.update(session.id, {
        expected_count: expected !== '' ? parseInt(expected) : null,
        registered_count: registered !== '' ? parseInt(registered) : null,
        attended_count: actual !== '' ? parseInt(actual) : null,
      });
      toast.success('Attendance updated');
      setEditing(false);
      onSave();
    } catch {
      toast.error('Failed to update attendance');
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    setStatusSaving(true);
    try {
      await sessionsAPI.update(session.id, { status: newStatus });
      toast.success('Session status updated');
      onSave();
    } catch {
      toast.error('Failed to update session status');
    } finally {
      setStatusSaving(false);
    }
  };

  return (
    <TableRow data-testid={`attendance-row-${session.id}`}>
      <TableCell>
        <p className="font-medium text-sm">{session.title}</p>
        {session.course_title && <p className="text-xs text-muted-foreground">{session.course_title}</p>}
      </TableCell>
      <TableCell className="text-sm">
        {session.session_date ? format(new Date(session.session_date), 'MMM d, yyyy') : '—'}
      </TableCell>
      <TableCell>
        <Select value={session.status} onValueChange={handleStatusChange} disabled={statusSaving}>
          <SelectTrigger
            className="h-7 w-[130px] border-0 p-0 focus:ring-0 [&>svg]:ml-1"
            data-testid={`attendance-status-select-${session.id}`}
          >
            <Badge className={`${SESSION_STATUS_COLORS[session.status] || 'bg-gray-100 text-gray-800'} capitalize text-xs`}>
              <SelectValue />
            </Badge>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="scheduled">Scheduled</SelectItem>
            <SelectItem value="in_progress">In Progress</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
            <SelectItem value="cancelled">Cancelled</SelectItem>
          </SelectContent>
        </Select>
      </TableCell>
      <TableCell className="text-right">
        {editing ? (
          <Input
            type="number"
            min="0"
            value={expected}
            onChange={(e) => setExpected(e.target.value)}
            className="w-20 text-right ml-auto"
            data-testid={`expected-input-${session.id}`}
          />
        ) : (
          <span className="font-medium">{session.expected_count ?? '—'}</span>
        )}
      </TableCell>
      <TableCell className="text-right">
        {editing ? (
          <Input
            type="number"
            min="0"
            value={registered}
            onChange={(e) => setRegistered(e.target.value)}
            className="w-20 text-right ml-auto"
            data-testid={`registered-input-${session.id}`}
          />
        ) : (
          <span className="font-medium text-blue-600">{session.registered_count ?? '—'}</span>
        )}
      </TableCell>
      <TableCell className="text-right">
        {editing ? (
          <div className="flex items-center gap-2 justify-end">
            <Input
              type="number"
              min="0"
              value={actual}
              onChange={(e) => setActual(e.target.value)}
              className="w-20 text-right"
              data-testid={`actual-input-${session.id}`}
            />
            <Button size="sm" onClick={handleSave} disabled={saving} className="bg-lime text-lime-foreground hover:bg-lime/90">
              {saving ? '...' : 'Save'}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>Cancel</Button>
          </div>
        ) : (
          <div className="flex items-center gap-2 justify-end">
            <span className={`font-medium ${session.attended_count ? 'text-green-600' : ''}`}>
              {session.attended_count ?? '—'}
            </span>
            <Button variant="ghost" size="sm" onClick={() => setEditing(true)} data-testid={`edit-attendance-${session.id}`}>
              <Edit2 className="w-3.5 h-3.5" />
            </Button>
          </div>
        )}
      </TableCell>
    </TableRow>
  );
};

export default AttendanceRow;
